import { ReactElement, useState } from 'react';
import Colors from '@domain/constant/colors';

import classnames from 'classnames/bind';
import styles from '../introduce.module.scss';
const cx = classnames.bind(styles);

const Arrow = () => {
  return (
    <svg
      className={cx('arrowWrap')}
      width="20"
      height="12"
      viewBox="0 0 20 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        className={cx('arrow')}
        d="M1.31938 0.92282C1.13869 0.732627 0.838038 0.724918 0.647845 0.905601C0.457652 1.08628 0.449943 1.38694 0.630626 1.57713L9.65311 11.0745C9.65636 11.078 9.65967 11.0814 9.66304 11.0848C9.75557 11.178 9.87767 11.225 10 11.225C10.0697 11.225 10.1395 11.2097 10.204 11.1791C10.2522 11.1562 10.2973 11.1247 10.337 11.0848C10.3403 11.0814 10.3436 11.078 10.3469 11.0745L19.3694 1.57713C19.5501 1.38694 19.5423 1.08628 19.3522 0.905601C19.162 0.724919 18.8613 0.732627 18.6806 0.92282L10 10.0603L1.31938 0.92282Z"
        fill={Colors.C_COOL_GRAY_60}
      />
    </svg>
  );
};

interface DataProps {
  title: string;
  contents: ReactElement;
}

const data: Array<DataProps> = [
  {
    title: '역검리포트는 정확한가요?',
    contents: (
      <span>
        역검리포트는 구직자를 판단하거나 결정하는 정답이 아닙니다. 일반적인 인적성검사나 대면면접 보다 훨씬{' '}
        <em className={cx('bold')}>더 합리적</em>이고{' '}
        <em className={cx('bold')}>객관적인 관점으로 구직자 역량을 분석</em>하고 이를 통해 자신의 역량에 대한 합리적
        이해를 통해 <em className={cx('bold')}>성장의 방향을 설정하는데 도움을 주는 모범 답안</em>의 역할이라고
        생각합니다.
      </span>
    ),
  },
  {
    title: '왜 12가지 유형인가요?',
    contents: (
      <>
        <span>
          천인천색(千人千色), 만인만색(萬人萬色)이라는 말이 있습니다. 사람의 얼굴과 개성, 성격과 성향, 능력과 역량 등은
          모두 다르며 똑같은 사람은 없다는 의미입니다. 한 사람 한 사람은 그 자체로 유일한 존재이며 각자의 아름다운 결을
          가진 인격체로서 존중받아야 합니다. 만약 잡다가 사람을 평가하는 의도였다면 78억 개의 유형이 있어야 합니다.
        </span>
        <span>
          그러나 <em className={cx('bold')}>잡다는 사람의 역량에 주목하고 역량중심 사회를 만들어 가기 위해</em> 역검을
          통해 핵심역량을 분석하고 역량프로파일을 통해 나온 경향성에 따라 개념화를 진행하였습니다. 이후,{' '}
          <em className={cx('bold')}>
            빅데이터 분석으로 역량별 차이점을 구분하고 분류하여 역량검사 결과를 12가지로 유형으로 범주화하였습니다.
          </em>
        </span>
      </>
    ),
  },
  {
    title: '강점과 약점은 무슨 의미인가요?',
    contents: (
      <>
        <span>
          잡다는 역랑중심 사회를 위해서 역량이라는 가장 중요한 공통분모를 중심으로 취업과 채용을 돕고자 개발하였습니다.
        </span>
        <span>
          우리는 자신의 강점은 강화하고 약점은 개선해야 한다는 말을 많이 듣습니다. 그러나 우리가{' '}
          <em className={cx('bold')}>
            행복한 삶을 살기 위해서는 약점보다는 강점을 바탕으로 우리에게 주어지는 기회와 역할에서 역량을 최대한
            발휘하여 성공경험을 할 때 행복할 수 있습니다.
          </em>
        </span>
        <span>
          기업은 좋은 인재를 채용하여 조직의 좋은 성과를 통해 기업이 성장하기를 원합니다. 그러므로{' '}
          <em className={cx('bold')}>기업은 지원자의 약점을 보고 인재를 판단하고 인재를 채용하지는 않습니다.</em> 기업은
          지원자의 강점을 이용해 조직의 성과를 만들고 이익을 얻는 것이 중요하기 때문입니다. 그럼에도 지원자들은 자신의
          강점보다 약점에 더 신경을 씁니다. 강점을 계속해서 ‘강화’하고 ‘함양’하는 것은 매우 중요하며 약점은 개선하거나
          보완하는 것이 아니라 ‘조심’하는 것입니다.
        </span>
        <span>
          약점으로 인해 내가 하고자 하는 일이 그릇되지 않도록 계속해서 주시하고 조심하도록 하며{' '}
          <em className={cx('bold')}>
            강점을 기반으로 더 능동적이고 주체적으로 생각하고 행동하는 것이 행복한 삶의 원동력입니다.
          </em>
        </span>
      </>
    ),
  },
];

interface ListItemProps {
  index: number;
  title: string;
  contents: ReactElement;
}

const ListItem = ({ index, title, contents }: ListItemProps) => {
  const [open, setOpen] = useState(index === 0);
  return (
    <li className={cx('listItem', { on: open })} onClick={() => setOpen(!open)}>
      <span className={cx('listItemTitle')}>
        {title}
        <Arrow />
      </span>
      <div className={cx('listItemContents')}>{contents}</div>
    </li>
  );
};

const List = () => {
  const list = data.map(({ title, contents }, index) => {
    return <ListItem key={index} index={index} title={title} contents={contents} />;
  });

  return <ul className={cx('questionList')}>{list}</ul>;
};

const Question = () => {
  return (
    <div className={cx('questionWrap')}>
      <div className={cx('question')}>
        <div className={cx('questionTextWrap')}>
          <span className={cx('questionDeco')}>여기서 잠깐!</span>
          <div className={cx('questionTitleWrap')}>
            <span className={cx('questionTitle')}>
              <span className={cx('textEmphasis')}>역검</span> 리포트에 대한
              <br />
              궁금증을 해결해드립니다.
            </span>
          </div>
        </div>
        <ul className={cx('questionList')}>
          <List />
        </ul>
      </div>
    </div>
  );
};

export default Question;
